import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminUsersQuery } from '../../queries/appQueries';
import type { PageResponse } from '../../services/userManagementService';
import {
  Box,
  Stack,
  TextInput,
  Select,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Pagination,
  Card,
  Modal,
  Text,
  useMantineColorScheme,
  Tooltip,
  Menu,
  SimpleGrid,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconEye,
  IconEdit,
  IconTrash,
  IconUserCheck,
  IconUserX,
  IconKey,
  IconCreditCard,
  IconDotsVertical,
  IconChevronRight,
  IconRefresh,
  IconUsers,
  IconUserPlus,
  IconUserCog,
  IconCalendar,
  IconStar,
  IconCrown,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import AdminPageHeader from '../../components/AdminPageHeader';
import PageTransition from '../../components/animations/PageTransition';
import MedicalLoader from '../../components/animations/MedicalLoader';
import ManageSubscriptionModal from '../../components/admin/ManageSubscriptionModal';
import {
  userManagementService,
  UserManagement,
  UserFilters,
} from '../../services/userManagementService';

const UsersPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const queryClient = useQueryClient();
  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const colActiva = dark ? '#86b8a6' : '#4f8a74';
  const colInactiva = dark ? '#d29ba0' : '#b5565e';
  const colAmber = dark ? '#cdb487' : '#a07d3c';
  const colViolet = dark ? '#a99bd1' : '#9385c0';
  const outlineBtn = { root: { borderColor: textColor, color: textColor, backgroundColor: 'transparent' } };
  // Fila expandida (para mostrar lo no esencial: teléfono, fecha de registro…).
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<string[]>([]);

  // (placeholder para mantener el shape original del objeto stats abajo)
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    paidSubscriptions: 0,
    customSubscriptions: 0,
  });

  // Search input state (separate from filters for debouncing)
  const [searchInput, setSearchInput] = useState('');

  // Filtros y búsqueda
  const [filters, setFilters] = useState<UserFilters>({
    page: 0,
    size: 10,
    search: '',
    subscriptionStatus: undefined,
    activo: undefined,
    fechaDesde: undefined,
    fechaHasta: undefined,
    sortBy: 'creadoEn',
    sortDir: 'desc',
  });

  // Estado para modales
  const [selectedUser, setSelectedUser] = useState<UserManagement | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [manageSubscriptionModalOpen, setManageSubscriptionModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resetPasswordConfirmOpen, setResetPasswordConfirmOpen] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [fullAccessConfirmOpen, setFullAccessConfirmOpen] = useState(false);
  const [fullAccessLoading, setFullAccessLoading] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput, page: 0 }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Listado de usuarios via React Query (con placeholderData para no parpadear).
  const usersQueryResult = useQuery(adminUsersQuery(filters));
  const usersResponse = usersQueryResult.data as PageResponse<UserManagement> | undefined;
  const loading = usersQueryResult.isFetching;
  const usersQueryError = usersQueryResult.error;
  const users = usersResponse?.content ?? [];
  const totalPages = usersResponse?.totalPages ?? 0;
  const totalElements = usersResponse?.totalElements ?? 0;

  useEffect(() => {
    if (usersQueryError) {
      console.error('Error al cargar usuarios:', usersQueryError);
      notifications.show({
        title: 'Error',
        message: 'No se pudieron cargar los usuarios',
        color: 'red',
      });
    }
  }, [usersQueryError]);

  // Stats derivados del listado actual.
  useEffect(() => {
    if (!usersResponse) return;
    const activeCount = usersResponse.content.filter((u) => u.activo).length;
    const paidCount = usersResponse.content.filter(
      (u) => u.subscriptionPlanName && u.subscriptionPlanName !== 'Free'
    ).length;
    const customCount = usersResponse.content.filter((u) => u.isCustomSubscription).length;
    setStats({
      totalUsers: usersResponse.totalElements,
      activeUsers: activeCount,
      paidSubscriptions: paidCount,
      customSubscriptions: customCount,
    });
  }, [usersResponse]);

  const refreshUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
  };

  useEffect(() => {
    loadSubscriptionPlans();
  }, []);

  const loadSubscriptionPlans = async () => {
    try {
      const plans = await userManagementService.getSubscriptionPlans();
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error('Error al cargar planes de suscripción:', error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters({ ...filters, [key]: value, page: 0 });
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page: page - 1 });
  };

  const handleToggleStatus = async (user: UserManagement) => {
    try {
      await userManagementService.toggleStatus(user.id, !user.activo);
      notifications.show({
        title: 'Éxito',
        message: `Usuario ${!user.activo ? 'activado' : 'suspendido'} correctamente`,
        color: 'green',
      });
      refreshUsers();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo cambiar el estado del usuario',
        color: 'red',
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await userManagementService.deleteUser(selectedUser.id);
      notifications.show({
        title: 'Éxito',
        message: 'Usuario eliminado correctamente',
        color: 'green',
      });
      setDeleteConfirmOpen(false);
      refreshUsers();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo eliminar el usuario',
        color: 'red',
      });
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setResetPasswordLoading(true);
    try {
      await userManagementService.resetPassword(selectedUser.id);
      notifications.show({
        title: 'Contraseña restablecida',
        message: `Se envió la nueva contraseña al correo de ${selectedUser.email}`,
        color: 'green',
      });
      setResetPasswordConfirmOpen(false);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo restablecer la contraseña',
        color: 'red',
      });
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const hasFullAccess = (user: UserManagement | null) =>
    !!user && (user.subscriptionPlanName?.toLowerCase() === 'cortesía' ||
               user.subscriptionStatus?.toLowerCase() === 'cortesía');

  const handleToggleFullAccess = async () => {
    if (!selectedUser) return;
    setFullAccessLoading(true);
    const granting = !hasFullAccess(selectedUser);
    try {
      if (granting) {
        await userManagementService.grantFullAccess(selectedUser.id);
      } else {
        await userManagementService.revokeFullAccess(selectedUser.id);
      }
      notifications.show({
        title: granting ? 'Acceso completo otorgado' : 'Acceso completo revocado',
        message: granting
          ? `${selectedUser.email} ahora tiene acceso completo (Cortesía)`
          : `${selectedUser.email} fue movido a plan FREE`,
        color: 'green',
      });
      setFullAccessConfirmOpen(false);
      refreshUsers();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: granting ? 'No se pudo otorgar el acceso' : 'No se pudo revocar el acceso',
        color: 'red',
      });
    } finally {
      setFullAccessLoading(false);
    }
  };

  const handleViewDetails = async (user: UserManagement) => {
    try {
      // Cargar detalles del usuario y métricas en paralelo
      const [userDetails, metricas] = await Promise.all([
        userManagementService.getUserDetails(user.id),
        userManagementService.getUserMetrics(user.id)
      ]);
      
      setSelectedUser({ ...userDetails, metricas });
      setDetailsModalOpen(true);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudieron cargar los detalles',
        color: 'red',
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: es });
    } catch {
      return 'N/A';
    }
  };

  const getSubscriptionBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      Free: 'gray',
      Trial: 'blue',
      Standard: 'green',
      Premium: 'violet',
    };
    return colors[status] || 'gray';
  };

  if (loading && users.length === 0) {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MedicalLoader type="heartbeat" size="lg" text="Cargando usuarios..." />
      </Box>
    );
  }

  return (
    <PageTransition type="medical" duration={800}>
      <Box style={{ flex: 1, padding: '32px', overflow: 'hidden', overflowY: 'auto' }}>
        <Stack gap="lg">
          <AdminPageHeader
            title="Gestión de Usuarios"
            subtitle="Administra los usuarios del sistema"
          />

          {/* Stats Cards (paleta sobria) */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            {[
              { icon: IconUsers, label: 'Total Usuarios', value: stats.totalUsers },
              { icon: IconUserCheck, label: 'Usuarios Activos', value: stats.activeUsers },
              { icon: IconCreditCard, label: 'Suscripciones Pagadas', value: stats.paidSubscriptions },
              { icon: IconStar, label: 'Suscripciones Custom', value: stats.customSubscriptions },
            ].map((s) => (
              <Card key={s.label} withBorder radius="lg" p="md" style={cardStyle}>
                <Group gap="xs" wrap="nowrap">
                  <s.icon size={20} color={textColor} style={{ flexShrink: 0 }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" style={{ color: mutedColor }}>{s.label}</Text>
                    <Text size="xl" fw={700} style={{ color: textColor }}>{s.value}</Text>
                  </Box>
                </Group>
              </Card>
            ))}
          </SimpleGrid>

          {/* Filtros y Búsqueda */}
          <Card withBorder radius="lg" p="lg" style={cardStyle}>
            <Stack gap="md">
              <Group grow>
                <TextInput
                  placeholder="Buscar por nombre, email, username o teléfono"
                  leftSection={<IconSearch size={16} />}
                  value={searchInput}
                  onChange={(e) => handleSearch(e.currentTarget.value)}
                />
                <Button
                  leftSection={<IconRefresh size={16} />}
                  onClick={refreshUsers}
                  variant="default"
                  styles={outlineBtn}
                >
                  Actualizar
                </Button>
              </Group>

              <Group grow>
                <Select
                  placeholder="Suscripción"
                  leftSection={<IconFilter size={16} />}
                  clearable
                  data={subscriptionPlans.map(plan => ({ value: plan, label: plan }))}
                  value={filters.subscriptionStatus}
                  onChange={(value) => handleFilterChange('subscriptionStatus', value)}
                />
                <Select
                  placeholder="Estado"
                  clearable
                  data={[
                    { value: 'true', label: 'Activo' },
                    { value: 'false', label: 'Inactivo' },
                  ]}
                  value={filters.activo === undefined ? null : filters.activo.toString()}
                  onChange={(value) => handleFilterChange('activo', value === null ? undefined : value === 'true')}
                />
              </Group>
            </Stack>
          </Card>

          {/* Tabla de Usuarios */}
          <Card withBorder radius="lg" p="lg" style={cardStyle}>
            <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" withRowBorders style={{ fontSize: '13px', textAlign: 'left' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 36 }} />
                  <Table.Th style={{ fontSize: 12, color: mutedColor, fontWeight: 600 }}>Usuario</Table.Th>
                  <Table.Th style={{ fontSize: 12, color: mutedColor, fontWeight: 600 }}>Rol</Table.Th>
                  <Table.Th style={{ fontSize: 12, color: mutedColor, fontWeight: 600 }}>Suscripción</Table.Th>
                  <Table.Th style={{ fontSize: 12, color: mutedColor, fontWeight: 600 }}>Estado</Table.Th>
                  <Table.Th style={{ fontSize: 12, color: mutedColor, fontWeight: 600, textAlign: 'right' }}>Acciones</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((user) => {
                  const expanded = expandedUserId === user.id;
                  return (
                  <React.Fragment key={user.id}>
                  <Table.Tr>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => setExpandedUserId(expanded ? null : user.id)}
                        aria-label="Expandir"
                      >
                        <IconChevronRight size={14} style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }} />
                      </ActionIcon>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fw={600} style={{ color: textColor }}>
                        {user.nombre} {user.apellidos}
                      </Text>
                      <Text size="11px" style={{ color: mutedColor }}>
                        @{user.username} · {user.email}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fw={500} style={{ color: mutedColor }}>
                        {user.roles.map((r) => r.replace('ROLE_', '')).join(', ')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={6} wrap="wrap">
                          {hasFullAccess(user) ? (
                            <Text size="xs" fw={600} style={{ color: colAmber }}>Cortesía</Text>
                          ) : (
                            <Text size="xs" fw={600} style={{ color: textColor }}>
                              {user.subscriptionPlanName || user.subscriptionStatus}
                            </Text>
                          )}
                          {user.isCustomSubscription && !hasFullAccess(user) && (
                            <Text size="xs" fw={600} style={{ color: colViolet }}>Custom</Text>
                          )}
                        </Group>
                        {user.subscriptionEndDate && (
                          <Text size="10px" style={{ color: mutedColor }}>
                            {user.daysRemaining !== undefined && user.daysRemaining >= 0
                              ? `${user.daysRemaining} días restantes`
                              : 'Expirada'}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fw={600} style={{ color: user.activo ? colActiva : colInactiva }}>
                        {user.activo ? 'Activo' : 'Inactivo'}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Tooltip label="Ver detalles">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => handleViewDetails(user)}
                          >
                            <IconEye size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" size="sm">
                              <IconDotsVertical size={14} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => {
                                setSelectedUser(user);
                                setEditModalOpen(true);
                              }}
                            >
                              Editar
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconCreditCard size={14} />}
                              onClick={() => {
                                setSelectedUser(user);
                                setManageSubscriptionModalOpen(true);
                              }}
                            >
                              Gestionar Suscripción
                            </Menu.Item>
                            <Menu.Item
                              leftSection={user.activo ? <IconUserX size={14} /> : <IconUserCheck size={14} />}
                              onClick={() => handleToggleStatus(user)}
                            >
                              {user.activo ? 'Suspender' : 'Activar'}
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconKey size={14} />}
                              onClick={() => {
                                setSelectedUser(user);
                                setResetPasswordConfirmOpen(true);
                              }}
                            >
                              Resetear Contraseña
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconCrown size={14} />}
                              color={hasFullAccess(user) ? 'orange' : 'yellow'}
                              onClick={() => {
                                setSelectedUser(user);
                                setFullAccessConfirmOpen(true);
                              }}
                            >
                              {hasFullAccess(user) ? 'Revocar acceso completo' : 'Dar acceso completo'}
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconTrash size={14} />}
                              color="red"
                              onClick={() => {
                                setSelectedUser(user);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              Eliminar
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                  {expanded && (
                    <Table.Tr style={{ backgroundColor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)' }}>
                      <Table.Td />
                      <Table.Td colSpan={5}>
                        <Group gap="xl" wrap="wrap">
                          <div>
                            <Text size="10px" style={{ color: mutedColor }}>Teléfono</Text>
                            <Text size="xs" style={{ color: textColor }}>{user.telefono || '—'}</Text>
                          </div>
                          <div>
                            <Text size="10px" style={{ color: mutedColor }}>Fecha de registro</Text>
                            <Text size="xs" style={{ color: textColor }}>{formatDate(user.creadoEn)}</Text>
                          </div>
                          <div>
                            <Text size="10px" style={{ color: mutedColor }}>Email</Text>
                            <Text size="xs" style={{ color: textColor }}>{user.email}</Text>
                          </div>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
            </Table.ScrollContainer>

            {users.length === 0 && (
              <Box p="xl" ta="center">
                <Text c="dimmed">No se encontraron usuarios</Text>
              </Box>
            )}

            {totalPages > 1 && (
              <Box mt="lg" style={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  total={totalPages}
                  value={(filters.page || 0) + 1}
                  onChange={handlePageChange}
                  color="gray"
                  size="sm"
                />
              </Box>
            )}

            <Text size="xs" c="dimmed" mt="md" ta="center">
              Mostrando {users.length} de {totalElements} usuarios
            </Text>
          </Card>
        </Stack>
      </Box>

      {/* Modal de detalles */}
      <Modal
        opened={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        title="Detalles del Usuario"
        size="lg"
      >
        {selectedUser && (
          <Stack gap="md">
            <Group>
              <div>
                <Text size="sm" c="dimmed">Nombre Completo</Text>
                <Text fw={500}>{selectedUser.nombre} {selectedUser.apellidos}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="sm" c="dimmed">Email</Text>
                <Text>{selectedUser.email}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Teléfono</Text>
                <Text>{selectedUser.telefono}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="sm" c="dimmed">Username</Text>
                <Text>@{selectedUser.username}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Suscripción</Text>
                <Badge color={getSubscriptionBadgeColor(selectedUser.subscriptionStatus)}>
                  {selectedUser.subscriptionStatus}
                </Badge>
              </div>
            </Group>
            
            {selectedUser.metricas && (
              <>
                <Text size="lg" fw={600} mt="md">Métricas de Rendimiento</Text>
                <Group grow>
                  <Card withBorder>
                    <Text size="sm" c="dimmed">Promedio Calificaciones</Text>
                    <Text size="xl" fw={700}>{selectedUser.metricas.promedioCalificaciones}%</Text>
                  </Card>
                  <Card withBorder>
                    <Text size="sm" c="dimmed">Tiempo Total Estudio</Text>
                    <Text size="xl" fw={700}>{Math.floor(selectedUser.metricas.tiempoTotalEstudio / 60)}h</Text>
                  </Card>
                </Group>
                <Group grow>
                  <Card withBorder>
                    <Text size="sm" c="dimmed">Progreso General</Text>
                    <Text size="xl" fw={700}>{selectedUser.metricas.progresoGeneral}%</Text>
                  </Card>
                  <Card withBorder>
                    <Text size="sm" c="dimmed">Última Actividad</Text>
                    <Text size="sm">
                      {selectedUser.metricas.ultimaActividad 
                        ? formatDate(selectedUser.metricas.ultimaActividad) 
                        : 'Sin actividad'}
                    </Text>
                  </Card>
                </Group>
              </>
            )}
          </Stack>
        )}
      </Modal>

      {/* Modal de edición */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Editar Usuario"
      >
        {selectedUser && (
          <Stack gap="md">
            <TextInput
              label="Nombre"
              defaultValue={selectedUser.nombre}
              onChange={(e) => setSelectedUser({ ...selectedUser, nombre: e.currentTarget.value })}
            />
            <TextInput
              label="Apellidos"
              defaultValue={selectedUser.apellidos}
              onChange={(e) => setSelectedUser({ ...selectedUser, apellidos: e.currentTarget.value })}
            />
            <TextInput
              label="Email"
              defaultValue={selectedUser.email}
              onChange={(e) => setSelectedUser({ ...selectedUser, email: e.currentTarget.value })}
            />
            <TextInput
              label="Teléfono"
              defaultValue={selectedUser.telefono}
              onChange={(e) => setSelectedUser({ ...selectedUser, telefono: e.currentTarget.value })}
            />
            <Select
              label="Rol"
              data={[
                { value: 'ADMIN', label: 'Administrador' },
                { value: 'ESTUDIANTE', label: 'Estudiante' },
              ]}
              value={selectedUser.roles[0]}
              onChange={(value) => {
                if (value) {
                  setSelectedUser({ ...selectedUser, roles: [value] });
                }
              }}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setEditModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  try {
                    // Actualizar datos básicos del usuario
                    await userManagementService.updateUser(selectedUser.id, {
                      nombre: selectedUser.nombre,
                      apellidos: selectedUser.apellidos,
                      email: selectedUser.email,
                      telefono: selectedUser.telefono,
                    });
                    
                    // Si el rol cambió, actualizarlo
                    const originalUser = users.find(u => u.id === selectedUser.id);
                    if (originalUser && originalUser.roles[0] !== selectedUser.roles[0]) {
                      await userManagementService.changeRole(selectedUser.id, selectedUser.roles[0]);
                    }
                    
                    notifications.show({
                      title: 'Éxito',
                      message: 'Usuario actualizado correctamente',
                      color: 'green',
                    });
                    setEditModalOpen(false);
                    refreshUsers();
                  } catch (error) {
                    notifications.show({
                      title: 'Error',
                      message: 'No se pudo actualizar el usuario',
                      color: 'red',
                    });
                  }
                }}
              >
                Guardar
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Modal de gestión de suscripción */}
      <ManageSubscriptionModal
        opened={manageSubscriptionModalOpen}
        onClose={() => {
          setManageSubscriptionModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSuccess={() => {
          refreshUsers();
        }}
      />

      {/* Modal de confirmación de eliminación */}
      <Modal
        opened={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Confirmar eliminación"
      >
        <Text>¿Estás seguro de que deseas eliminar a este usuario?</Text>
        <Group mt="md" justify="flex-end">
          <Button variant="light" onClick={() => setDeleteConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleDeleteUser}>
            Eliminar
          </Button>
        </Group>
      </Modal>

      {/* Modal de confirmación de reseteo de contraseña */}
      <Modal
        opened={resetPasswordConfirmOpen}
        onClose={() => !resetPasswordLoading && setResetPasswordConfirmOpen(false)}
        title="Restablecer contraseña"
      >
        <Text>
          Se generará una nueva contraseña para{' '}
          <Text span fw={600}>
            {selectedUser?.nombre} {selectedUser?.apellidos}
          </Text>{' '}
          y se enviará a{' '}
          <Text span fw={600}>
            {selectedUser?.email}
          </Text>
          . La contraseña actual dejará de funcionar.
        </Text>
        <Text mt="sm" size="sm" c="dimmed">
          ¿Deseas continuar?
        </Text>
        <Group mt="md" justify="flex-end">
          <Button
            variant="light"
            onClick={() => setResetPasswordConfirmOpen(false)}
            disabled={resetPasswordLoading}
          >
            Cancelar
          </Button>
          <Button color="blue" onClick={handleResetPassword} loading={resetPasswordLoading}>
            Restablecer
          </Button>
        </Group>
      </Modal>

      {/* Modal de confirmación de acceso completo */}
      <Modal
        opened={fullAccessConfirmOpen}
        onClose={() => !fullAccessLoading && setFullAccessConfirmOpen(false)}
        title={hasFullAccess(selectedUser) ? 'Revocar acceso completo' : 'Dar acceso completo'}
      >
        {hasFullAccess(selectedUser) ? (
          <Text>
            Se revocará el acceso completo de{' '}
            <Text span fw={600}>
              {selectedUser?.nombre} {selectedUser?.apellidos}
            </Text>
            . El usuario quedará en el plan{' '}
            <Text span fw={600}>FREE</Text>{' '}
            y perderá acceso a los features premium.
          </Text>
        ) : (
          <Text>
            Se otorgará acceso completo (Cortesía) a{' '}
            <Text span fw={600}>
              {selectedUser?.nombre} {selectedUser?.apellidos}
            </Text>
            . El usuario tendrá todos los features premium sin pagar, hasta que un admin lo revoque.
          </Text>
        )}
        <Text mt="sm" size="sm" c="dimmed">¿Deseas continuar?</Text>
        <Group mt="md" justify="flex-end">
          <Button
            variant="light"
            onClick={() => setFullAccessConfirmOpen(false)}
            disabled={fullAccessLoading}
          >
            Cancelar
          </Button>
          <Button
            color={hasFullAccess(selectedUser) ? 'orange' : 'yellow'}
            onClick={handleToggleFullAccess}
            loading={fullAccessLoading}
          >
            {hasFullAccess(selectedUser) ? 'Revocar' : 'Otorgar'}
          </Button>
        </Group>
      </Modal>
    </PageTransition>
  );
};

export default UsersPage;
